import { Box, Checkbox, useHeldKey, useValueRef } from '@timelyapp/react';
import classNames from 'classnames';
import CurrencyFormat from 'components/CurrencyFormat';
import DurationFormat from 'components/DurationFormat';
import DiagnosticResourceId from 'components/dev-tools/DiagnosticResourceId';
import HourStateLabel from 'components/HourStateLabel';
import { HourSyncStatus } from 'components/HourSyncStatus';
import { Billable, Billed, Brain, Tag } from 'components/Icons';
import { Pulse } from 'components/Pulse';
import RichTooltip from 'components/RichTooltip';
import Truncate from 'components/Truncate';
import { HOUR_LAYOUT } from 'constants/hour';
import type { HourTotals } from 'factories/calendar';
import React, { useCallback } from 'react';
import type { ConnectDragPreview, ConnectDragSource, ConnectDropTarget } from 'react-dnd';
import { useSelector } from 'react-redux';
import { getHourStateById } from 'redux/modules/hourStates';
import { defaultState, defaultStateId } from 'redux/modules/hourStates.constants';
import type { IState } from 'redux/state';
import type { HourAsCard, HourAsCardUI } from 'types/hour';
import type { SuggestionAsCard } from 'types/suggestion';
import type { HourSyncStatus as HourSyncStatusType } from 'types/syncStatus';
import type { ApplyDefaultProps, Merge } from 'types/utility';
import { formatCurrency } from 'utils/currency';
import { hourCardSizes } from 'utils/hours';
import { STOP_PROPAGATION } from 'utils/react';
import { useIsMobile } from 'utils/viewport';
import styles from './Hour.module.scss';
import { HourClock } from './HourClock';

export interface PropsExceptHour {
  onClick?: (id: number) => unknown;
  onToggleSelected?: (id: number, selected: boolean, shiftHeld: boolean) => unknown;
  shouldAlwaysUpdate?: boolean;
  sizeBasedOnDuration?: boolean; // defaultProp'd,
  staticSize?: boolean;
  /** Are we animating this card? (usually height) */
  isAnimated?: boolean; // defaultProp'd,
  /** While resizing */
  isResizing?: boolean;
  /** While Hour is being dragged -- see HourDragDrop* */
  isDragging?: boolean;
  /** Is the hour currently being edited? */
  isEditing?: boolean;
  isSingleDayView?: boolean;
  /** For use in shouldComponentUpdate -- if component doesn't update, look here first! */
  cacheBuster?: null | string;
  connectDragPreview?: ConnectDragPreview;
  connectDragSource?: ConnectDragSource;
  connectDropTarget?: ConnectDropTarget;
  onStopTimer?: (hourId: number) => void;
  onAcceptHourSuggestedDuration?: (hourId: number, seconds: number) => void;
  draggedHour?: {
    hour: HourAsCard;
  };
  isOver?: boolean;
  isLiveUpdated?: boolean;
  actions?: React.ReactNode;
  showDebugInfo?: boolean;
  noMargin?: boolean;
  isModifyBillableFeatureEnabled?: boolean;
  hourTotals?: HourTotals;
  showMemories?: boolean;
  isDayLocked?: boolean;
  isDayLockingFeatureEnabled?: boolean;
  syncStatus?: Array<HourSyncStatusType> | null;
}

export type Props = PropsExceptHour & {
  hour: HourAsCard | SuggestionAsCard;
};

type CardIntersection = Partial<Merge<SuggestionAsCard, HourAsCard>>;

const defaultProps = {
  sizeBasedOnDuration: true,
  staticSize: false,
  isAnimated: true,
  isDragging: false,
  isLiveUpdated: false,
};

export type PropsWithAppliedDefaults = ApplyDefaultProps<Props, typeof defaultProps>;

const HourStateIconWrapper = React.memo<{ stateId?: number | null }>(props => {
  const state =
    useSelector((state: IState) => getHourStateById(state, props.stateId || defaultStateId)) ??
    defaultState;

  return (
    <RichTooltip
      childrenClassName={styles.iconTooltipWrapper}
      position="top"
      tooltip={<HourStateLabel state={state} size="xsmall" className={styles.stateLabel} />}
      className={styles.tooltip}
    >
      <HourStateLabel state={state} iconOnly size="xsmall" className={styles.stateIcon} />
    </RichTooltip>
  );
});

export const Hour: React.FC<Props> = ({
  actions,
  connectDragPreview = element => element as React.ReactElement,
  hour,
  hourTotals,
  isAnimated = true,
  isDragging = false,
  isLiveUpdated = false,
  isResizing,
  isSingleDayView,
  noMargin,
  onAcceptHourSuggestedDuration,
  onClick,
  onToggleSelected,
  onStopTimer,
  sizeBasedOnDuration = true,
  staticSize = false,
  showMemories = false,
  syncStatus,
}) => {
  const { projectCurrency: currency, ui } = hour;
  const isMobile = useIsMobile();

  const isSelectionMode = (ui as HourAsCardUI).isSelectionMode;
  const isSelected = (ui as HourAsCardUI).isSelected;

  // Disable selection on mobile
  const canSelect = !isMobile && isSelected != null && onToggleSelected != null;

  const shiftHeld = useHeldKey('Shift', true);
  const shiftHeldRef = useValueRef(shiftHeld);
  const handleToggleSelected = useCallback(
    (checked: boolean) => {
      onToggleSelected?.(hour.id, checked, shiftHeldRef.current);
    },
    [hour.id, onToggleSelected, shiftHeldRef]
  );

  const handleClick = useCallback(() => {
    if (isSelectionMode || shiftHeldRef.current) {
      if (canSelect) {
        onToggleSelected?.(hour.id, !isSelected, shiftHeldRef.current);
      }
      return;
    }
    onClick?.(hour.id);
  }, [hour.id, isSelected, isSelectionMode, onClick, onToggleSelected, shiftHeldRef, canSelect]);

  const handlePauseTimer = useCallback(() => {
    onStopTimer?.(hour.id);
  }, [hour.id, onStopTimer]);

  const handleAcceptSuggestedDuration = useCallback(
    (seconds: number) => {
      onAcceptHourSuggestedDuration?.(hour.id, seconds);
    },
    [hour.id, onAcceptHourSuggestedDuration]
  );

  const isJustPlanned = !hour.numLoggedSecondsExact && !!hour.numPlannedSeconds;
  const showBackgroundPattern = isJustPlanned || ui.isSuggestion;
  const sizes = hourCardSizes(hour, sizeBasedOnDuration, staticSize);

  const containerClass = classNames(styles.container, {
    [styles.animateHeightChange]: isAnimated,
    [styles.isDragging]: isDragging,
    [styles.isResizing]: isResizing,
    [styles.darkText]: !ui.isContrastReadable,
    darkText: !ui.isContrastReadable,
    [styles.noMargin]: noMargin,
    [styles.isFramelessSuggestion]: ui.isSuggestion && !isSingleDayView,
    [styles.selectionMode]: isSelectionMode,
  });

  const plannedHeight =
    sizes.plannedHeightPx < HOUR_LAYOUT.minHeight ? HOUR_LAYOUT.minHeight : sizes.plannedHeightPx;
  const loggedHeight =
    sizes.loggedHeightPx < HOUR_LAYOUT.minHeight ? HOUR_LAYOUT.minHeight : sizes.loggedHeightPx;
  // Set height based on planned time, if no logged time exists
  const height = isJustPlanned ? plannedHeight : loggedHeight;
  const containerStyle = {
    height,
    backgroundColor: hour.projectColor,
  };

  const { isBilled, isBillable, isInvoiced, tags, externalId, invoiceId, forecastId } =
    hour as CardIntersection;
  const isTagged = !!tags?.length;
  const isSuggestion = (hour as SuggestionAsCard).type === 'suggestion';

  return connectDragPreview(
    <div className={containerClass} style={containerStyle} onClick={handleClick}>
      <div className={styles.innerContainer}>
        <div className={styles.header}>
          <div
            className={classNames(styles.titles, {
              [styles.noteMissing]: !hour.note,
              [styles.smallestWithClient]:
                sizes.numClientLines > 0 && sizes.heightPx === HOUR_LAYOUT.minHeightWithClient,
              [styles.noteWithTwoLines]: sizes.numNoteLines > 1,
            })}
          >
            <Box.Row>
              <div className={classNames(styles.note, 'fs-mask')} data-testid="hour_title_hour">
                <Truncate lines={sizes.numNoteLines}>
                  <DiagnosticResourceId
                    resource={ui.isSuggestion ? 'suggestion' : 'hour'}
                    id={hour.id}
                  />
                  {ui.hasNoNote && <DiagnosticResourceId resource="project" id={hour.projectId} />}
                  {hour.note || hour.projectName || '...'}
                </Truncate>
              </div>
              {canSelect && (
                <div
                  className={classNames(styles.checkbox, { [styles.checked]: isSelected })}
                  onClick={STOP_PROPAGATION}
                >
                  <Checkbox
                    checked={isSelected}
                    onChangeChecked={handleToggleSelected}
                    data-testid="hour-checkbox"
                  />
                </div>
              )}
            </Box.Row>
            {sizes.numClientLines > 0 && (
              <small className={classNames(styles.projectAndClient, 'fs-mask')}>
                <Truncate data-testid="hour_entry_subheading_hour" lines={sizes.numClientLines}>
                  <span>
                    {!ui.isSuggestion && !isJustPlanned && ui.hasPlannedTime && (
                      <span>
                        Est <DurationFormat value={hour.numPlannedSeconds} valueType="seconds" /> ·
                      </span>
                    )}
                    {!ui.hasNoNote && (
                      <>
                        <DiagnosticResourceId resource="project" id={hour.projectId} />
                        {hour.projectName} ·{' '}
                      </>
                    )}
                    <DiagnosticResourceId resource="client" id={hour.clientId} /> {hour.clientName}
                    <div>
                      <DiagnosticResourceId resource="external hour id" id={externalId} />
                      <DiagnosticResourceId resource="invoice id" id={invoiceId} />
                      <DiagnosticResourceId resource="forecast id" id={forecastId} />
                    </div>
                  </span>
                </Truncate>
              </small>
            )}
          </div>
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
        <div data-testid="hour_log_hour" className={styles.stickyFooter}>
          {isBillable && !hour.isTimerRunning && hour.numLoggedMoney > 0 && (
            <small className={styles.money} data-testid="billable_hour">
              <RichTooltip
                fixed
                position="top-end"
                tooltip={`Hour is billable (${formatCurrency(currency, hour.numLoggedMoney)})`}
                childrenClassName={styles.iconTooltipWrapper}
                style={{ margin: 0 }} // Margin handled by <small>
                className={styles.tooltip}
              >
                <CurrencyFormat
                  className={styles.moneyValue}
                  value={hour.numLoggedMoney}
                  zeroPlaceholder="Billable"
                  currency={currency}
                  animated={isAnimated}
                />
              </RichTooltip>
            </small>
          )}
          <div className={styles.leftGroup}>
            {isBillable && !hour.isTimerRunning && hour.numLoggedMoney <= 0 && (
              <small className={styles.money} data-testid="billable_hour">
                <RichTooltip
                  position="top"
                  tooltip="Hour is billable"
                  childrenClassName={styles.iconTooltipWrapper}
                  className={styles.tooltip}
                  style={{ margin: 0 }} // Margin handled by <small>
                >
                  <Billable style={{ width: 14, height: 14 }} />
                </RichTooltip>
              </small>
            )}
            {isBilled && !isInvoiced && (
              <RichTooltip
                childrenClassName={styles.iconTooltipWrapper}
                position="top"
                tooltip="Hour is billed"
                className={styles.tooltip}
              >
                <Billed />
              </RichTooltip>
            )}
            {isInvoiced && (
              <RichTooltip
                childrenClassName={styles.iconTooltipWrapper}
                position="top"
                tooltip="Hour is billed via invoice"
                className={styles.tooltip}
              >
                <Billed />
              </RichTooltip>
            )}
            {isTagged && (
              <RichTooltip
                childrenClassName={styles.iconTooltipWrapper}
                position="top"
                tooltip="Hour is tagged"
                className={styles.tooltip}
              >
                <Tag />
              </RichTooltip>
            )}
            {isSuggestion && (
              <RichTooltip
                childrenClassName={styles.iconTooltipWrapper}
                position="top"
                tooltip="Draft suggested by Timesheet Assistant"
                className={styles.tooltip}
              >
                <Brain style={{ width: 14, height: 14 }} />
              </RichTooltip>
            )}
            {(hour as HourAsCard).ui.hasHourStatesFeature && (
              <HourStateIconWrapper stateId={hour.stateId} />
            )}
            {syncStatus != null && <HourSyncStatus status={syncStatus} />}
          </div>
          <div className={styles.clockGroup}>
            {isLiveUpdated && (
              <RichTooltip
                childrenClassName={styles.pulse}
                tooltip="Real-time updates"
                position="top"
                className={styles.tooltip}
              >
                <Pulse color={hour.projectName ? hour.projectColor : undefined} />
              </RichTooltip>
            )}
            <HourClock
              hour={hour}
              hourTotals={hourTotals}
              isResizing={isResizing}
              onLightBackground={!ui.isContrastReadable}
              onStopTimer={handlePauseTimer}
              onAcceptSuggestedDuration={handleAcceptSuggestedDuration}
              showSuggestedDuration={isSingleDayView && showMemories}
              tags={tags}
            />
          </div>
        </div>
      </div>
      {showBackgroundPattern && (
        <div
          className={classNames(styles.backgroundPatternOverlay, {
            [styles.isPlanned]: !ui.isSuggestion,
            [styles.isSuggested]: ui.isSuggestion,
          })}
          style={{ height }}
        />
      )}
    </div>
  );
};
